import React, { useEffect, useRef, useState } from 'react';

const MARK = /\[\[(.+?)\]\]/g;

export function parseLinkedText(text) {
  if (!text) return [];
  const parts = [];
  let last = 0;
  for (const match of text.matchAll(MARK)) {
    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) });
    }
    const bits = match[1].split('::');
    const label = bits[0];
    const rest = bits.slice(1);
    const links = [];
    if (rest.length === 1) {
      links.push({ label, href: rest[0] });
    } else {
      for (let i = 0; i < rest.length - 1; i += 2) {
        links.push({ label: rest[i], href: rest[i + 1] });
      }
    }
    parts.push({ type: 'link', label, links: links.filter((link) => link.label && link.href) });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ type: 'text', value: text.slice(last) });
  return parts;
}

const linkClass = "text-white underline decoration-red-600 decoration-2 underline-offset-[3px] hover:decoration-white focus-visible:outline-none focus-visible:decoration-white";

function LinkMark({ part }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const many = part.links.length > 1;

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!many) {
    return (
      <a href={part.links[0].href} target="_blank" rel="noopener noreferrer" className={linkClass}>
        {part.label}
      </a>
    );
  }

  return (
    <span ref={rootRef} className="relative">
      <button
        type="button"
        className={linkClass}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        {part.label}
        <svg className="ml-0.5 inline h-3 w-3 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M5.4 7.4 10 12l4.6-4.6 1.4 1.4L10 14.8 4 8.8z" />
        </svg>
      </button>
      {open && (
        <span role="menu" className="absolute left-0 top-full z-30 mt-2 flex min-w-[220px] flex-col rounded border border-white/30 bg-zinc-900 py-1 text-left text-white shadow-2xl">
          {part.links.map((link) => (
            <a
              key={link.href}
              role="menuitem"
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-sm font-semibold text-white no-underline hover:bg-white/10"
              onClick={() => setOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </span>
      )}
    </span>
  );
}

const LinkedText = ({ text }) => {
  const parts = parseLinkedText(text);
  return parts.map((part, index) => (
    part.type === 'text' ? part.value : <LinkMark key={index} part={part} />
  ));
};

export default LinkedText;
